/**
 * Labstep
 *
 * @module components/Entity/Search/Multi
 * @desc Generic component for rendering a search bar and multiple lists of entities
 */

import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { Flex } from 'labstep-web/core/Flex';
import { Paginator } from 'labstep-web/core/Paginator';
import { SearchHOC } from 'labstep-web/hoc/Search';
import type React from 'react';
import { createRef, useEffect, useState } from 'react';
import {
  Input,
  type Input as SemanticInput,
} from 'semantic-ui-react';
import type { IEntitySearchMultiProps } from './types';

export const EntitySearchMulti: React.FC<IEntitySearchMultiProps> = ({
  lists,
  staticLists,
  footer,
}) => {
  const inputRef = createRef<SemanticInput>();

  const [searchQuery, setSearchQuery] = useState<
    string | null | undefined
  >(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<
    string | null | undefined
  >(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(debouncedSearchQuery);
    }, 700);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchQuery]);

  return (
    <Flex grow column spacing="gap">
      <Input
        ref={inputRef}
        icon="search"
        iconPosition="left"
        placeholder="Search"
        onChange={(event) =>
          setDebouncedSearchQuery(event.target.value)
        }
        fluid
      />
      <Flex
        grow
        spacing="gap"
        column
        style={{ overflowY: 'auto' }}
        height={'100%'}
      >
        {staticLists && <div>{staticLists(searchQuery)}</div>}
        {lists &&
          lists.map((list) => (
            <Flex column key={list.key} style={{ minHeight: 'auto' }}>
              {list.header && <div>{list.header}</div>}
              <ParamsStateContextProvider value={{}}>
                <SearchHOC
                  entityName={list.entityName}
                  params={
                    searchQuery && searchQuery.length > 0
                      ? {
                          ...list.params,
                          [list.searchField]: searchQuery,
                        }
                      : list.params
                  }
                  isCursor
                >
                  {({
                    entities,
                    total,
                    nextCursor,
                    currentCursor,
                    read,
                    status,
                  }) => {
                    return (
                      <Paginator
                        read={read}
                        status={status}
                        nextCursor={nextCursor}
                        currentCursor={currentCursor}
                        total={total}
                      >
                        {entities.map((entity) =>
                          list.listItem(entity),
                        )}
                      </Paginator>
                    );
                  }}
                </SearchHOC>
              </ParamsStateContextProvider>
              {list.bottomAction && (
                <div>
                  {typeof list.bottomAction === 'function'
                    ? list.bottomAction(searchQuery)
                    : list.bottomAction}
                </div>
              )}
            </Flex>
          ))}
        {footer && <div>{footer(searchQuery)}</div>}
      </Flex>
    </Flex>
  );
};
